import { type FC } from 'react';
import clsx from 'clsx';
import { IconTrendingUp, IconTrendingDown, IconTarget, IconFilePlus } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { PROTECTED_ROUTES } from '../../../../constants/routes';

interface Props {
    name: string
    score: number
    type: 'danger' | 'success'
}

const SubjectResult: FC<Props> = ({ name, score, type }) => {
    const isSuccess = type === 'success';

    return (
        <div className={clsx(
            'relative flex flex-col h-full w-full',
            'p-2 md:p-4',
            'transition-all duration-300'
        )}>
            {/* Mobile: Compact horizontal layout */}
            <div className="md:hidden flex flex-col gap-2">
                {/* Header row with name and score */}
                <div className="flex items-center justify-between gap-2">
                    <h3 className={clsx(
                        'font-semibold text-sm flex-1 transition-colors duration-300',
                        'text-gray-900 dark:text-white',
                        'line-clamp-1'
                    )}>
                        {name}
                    </h3>
                    <div className={clsx(
                        'flex items-baseline gap-0.5 flex-shrink-0',
                        isSuccess
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                    )}>
                        <span className="text-2xl font-bold transition-colors duration-300 leading-none">
                            {score}
                        </span>
                        <span className="text-lg font-semibold transition-colors duration-300 leading-none">
                            %
                        </span>
                    </div>
                </div>

                {/* Progress bar */}
                <div className={clsx(
                    'w-full h-2 rounded-full overflow-hidden',
                    'bg-gray-200 dark:bg-surface-medium'
                )}>
                    <div
                        className={clsx(
                            'h-full rounded-full transition-all duration-500 ease-out',
                            isSuccess
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                : 'bg-gradient-to-r from-red-500 to-rose-500'
                        )}
                        style={{ width: `${score}%` }}
                    />
                </div>

                {/* Badge and button row */}
                <div className="flex items-center gap-2">
                    <div className={clsx(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-1',
                        isSuccess
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
                        'justify-center'
                    )}>
                        {isSuccess ? (
                            <>
                                <IconTrendingUp size={10} />
                                <span className="whitespace-nowrap">Mejor</span>
                            </>
                        ) : (
                            <>
                                <IconTrendingDown size={10} />
                                <span className="whitespace-nowrap">Refuerzo</span>
                            </>
                        )}
                    </div>
                    <Link
                        to={PROTECTED_ROUTES.START_TEST}
                        className={clsx(
                            'inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium',
                            'transition-all duration-300',
                            'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                            'hover:bg-blue-100 dark:hover:bg-blue-900/50',
                            'hover:shadow-md',
                            'flex-shrink-0',
                            'no-underline'
                        )}
                    >
                        <IconFilePlus size={12} strokeWidth={2} />
                        <span className="hidden sm:inline">Test</span>
                    </Link>
                </div>
            </div>

            {/* Desktop: Original vertical layout */}
            <div className="hidden md:flex flex-col h-full">
                {/* Header with subject name and badge - top */}
                <div className="flex-shrink-0">
                    <h3 className={clsx(
                        'font-semibold text-base mb-2 transition-colors duration-300',
                        'text-gray-900 dark:text-white',
                        'text-center'
                    )}>
                        {name}
                    </h3>
                </div>

                {/* Spacer to push content down */}
                <div className="flex-1" />

                {/* Score display with progress bar - bottom aligned */}
                <div className="space-y-3 flex-shrink-0">
                    {/* Large percentage */}
                    <div className="text-center">
                        <div className={clsx(
                            'flex items-baseline justify-center gap-1',
                            isSuccess
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                        )}>
                            <span className="text-5xl font-bold transition-colors duration-300 leading-none">
                                {score}
                            </span>
                            <span className="text-3xl font-semibold transition-colors duration-300 leading-none">
                                %
                            </span>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="space-y-1.5">
                        <div className={clsx(
                            'w-full h-3 rounded-full overflow-hidden',
                            'bg-gray-200 dark:bg-surface-medium'
                        )}>
                            <div
                                className={clsx(
                                    'h-full rounded-full transition-all duration-500 ease-out',
                                    isSuccess
                                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                        : 'bg-gradient-to-r from-red-500 to-rose-500'
                                )}
                                style={{ width: `${score}%` }}
                            />
                        </div>
                        <div className="flex items-center justify-center gap-1.5">
                            <IconTarget size={12} className={clsx(
                                'transition-colors duration-300',
                                'text-gray-400 dark:text-surface-light'
                            )} />
                            <span className={clsx(
                                'text-xs font-medium transition-colors duration-300',
                                'text-gray-600 dark:text-surface-light'
                            )}>
                                Preguntas acertadas
                            </span>
                        </div>
                        <div className={clsx(
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                            isSuccess
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
                            'w-full justify-center'
                        )}>
                            {isSuccess ? (
                                <>
                                    <IconTrendingUp size={12} />
                                    <span>Mejor rendimiento</span>
                                </>
                            ) : (
                                <>
                                    <IconTrendingDown size={12} />
                                    <span>Necesita refuerzo</span>
                                </>
                            )}
                        </div>
                        {/* Hacer test button */}
                        <Link
                            to={PROTECTED_ROUTES.START_TEST}
                            className={clsx(
                                'inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium',
                                'transition-all duration-300',
                                'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                                'hover:bg-blue-100 dark:hover:bg-blue-900/50',
                                'hover:shadow-md',
                                'w-full',
                                'no-underline'
                            )}
                        >
                            <IconFilePlus size={14} strokeWidth={2} />
                            <span>Hacer test</span>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { SubjectResult };
